import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Send } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { InShort } from "../components/Blocks";
import { SITE_DOMAIN } from "../content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: "correction", page: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you (your note has been recorded.");
      setForm({ name: "", email: "", topic: "correction", page: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: "var(--ink-muted)" }}>{label}</span>
      <input
        type={type}
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`contact-${key}-input`}
        className="w-full px-4 py-3 text-base border bg-transparent outline-none transition-colors focus:border-current"
        style={{ borderColor: "var(--line)", color: "var(--ink)", background: "var(--card)" }}
        {...extra}
      />
    </label>
  );

  return (
    <div data-testid="contact-page" className="max-w-[720px] mx-auto px-5 pt-12 md:pt-16">
      <Seo
        title="Contact & Corrections | U.CENTER Explained"
        meta="How to suggest a correction to this site: what to include, what changes fastest, and the form to send it."
        description="How to suggest a correction to this site: what to include, what changes fastest, and the form to send it."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact & Corrections | U.CENTER Explained",
          url: `${SITE_DOMAIN}/contact`,
        }}
      />

      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs mb-10" style={{ color: "var(--ink-muted)" }} data-testid="breadcrumbs">
        <Link to="/" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>Home</Link>
        <ChevronRight size={12} />
        <span style={{ color: "var(--ink)" }}>Contact</span>
      </nav>

      <Reveal y={20}>
        <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: "var(--accent)" }}>Corrections</p>
        <h1 className="font-serif-display font-light text-4xl md:text-6xl leading-[1.08] m-0" style={{ color: "var(--ink)" }}>Suggest a correction</h1>
        <InShort>
          Found something wrong, outdated, or unsourced in this review? This is the fastest way to change it. Corrections with a source attached get priority (that's the deal I made when I published.
        </InShort>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="prose-article mb-10">
          <h2 className="font-serif-display font-light text-3xl mt-12 mb-5" style={{ color: "var(--ink)" }}>What to include</h2>
          <ul className="pl-0 list-none space-y-3">
            {[
              "The page and the exact sentence you believe is wrong.",
              "What you believe it should say instead.",
              "A source; official documents carry the most weight.",
            ].map((item, i) => (
              <li key={i} className="flex gap-3 leading-relaxed">
                <span style={{ color: "var(--accent)" }}>, </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-base" style={{ color: "var(--ink-muted)" }}>
            This inbox is for factual corrections and source suggestions. It is not a support channel for U.CENTER or U-TOPIA. For account issues, use official channels on <a href="https://u.center" target="_blank" rel="noopener noreferrer">u.center</a>.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <form onSubmit={submit} data-testid="contact-form" className="space-y-6 p-8 border" style={{ background: "var(--card)", borderColor: "var(--line)" }}>
          <div className="grid sm:grid-cols-2 gap-6">
            {field("name", "Your name")}
            {field("email", "Email", "email")}
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: "var(--ink-muted)" }}>Topic</span>
              <select
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                data-testid="contact-topic-select"
                className="w-full px-4 py-3 text-base border outline-none"
                style={{ borderColor: "var(--line)", color: "var(--ink)", background: "var(--card)" }}
              >
                <option value="correction">Factual correction</option>
                <option value="source">Suggest a source</option>
                <option value="other">Something else</option>
              </select>
            </label>
            {field("page", "Page in question", "text", { required: false, placeholder: "e.g. /compensation-plan" })}
          </div>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: "var(--ink-muted)" }}>Message</span>
            <textarea
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message-input"
              className="w-full px-4 py-3 text-base border outline-none resize-y"
              style={{ borderColor: "var(--line)", color: "var(--ink)", background: "var(--card)" }}
            />
          </label>
          <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="outline-btn cursor-pointer disabled:opacity-50">
            {sending ? "Sending…" : "Send correction"} <Send size={15} />
          </button>
        </form>
      </Reveal>
    </div>
  );
}
